import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import { Checkbox, TextField } from '@mui/material'
import { Autocomplete as MuiAutocomplete, type AutocompleteProps as MuiAutocompleteProps } from '@mui/material'
import type { ReactNode } from 'react'

export type AutocompleteProps<
  Value = unknown,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
> = Omit<MuiAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>, 'renderInput'> & {
  checkboxes?: boolean
  error?: boolean
  helperText?: ReactNode
  label?: ReactNode
  renderInput?: MuiAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>['renderInput']
}

export function Autocomplete<
  Value = unknown,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>({
  checkboxes = false,
  disableCloseOnSelect,
  error,
  fullWidth = true,
  getOptionLabel,
  helperText,
  label,
  multiple,
  renderInput,
  renderOption,
  size = 'medium',
  ...props
}: AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>) {
  const defaultRenderInput: MuiAutocompleteProps<
    Value,
    Multiple,
    DisableClearable,
    FreeSolo
  >['renderInput'] = params => (
    <TextField
      {...params}
      error={error}
      fullWidth={fullWidth}
      helperText={helperText}
      label={label}
      variant="outlined"
    />
  )

  const defaultCheckboxRenderOption: MuiAutocompleteProps<
    Value,
    Multiple,
    DisableClearable,
    FreeSolo
  >['renderOption'] = (optionProps, option, state) => {
    const { key, ...restProps } = optionProps as typeof optionProps & { key: React.Key }
    const optionLabel = getOptionLabel ? getOptionLabel(option) : String(option)
    return (
      <li key={key} {...restProps}>
        <Checkbox
          checked={state.selected}
          checkedIcon={<CheckBoxIcon fontSize="small" />}
          disableRipple
          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
          size="small"
          sx={{
            // The theme sets color:transparent on MuiCheckbox to support the
            // custom CodesignCheckboxIndicator. Override it here so the raw
            // MUI SVG icons are visible in the Autocomplete option list.
            color: 'text.secondary',
            '&.Mui-checked': { color: 'primary.main' },
            mr: 1,
            p: 0.5,
          }}
          tabIndex={-1}
        />
        {optionLabel}
      </li>
    )
  }

  return (
    <MuiAutocomplete
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      disableCloseOnSelect={checkboxes ? true : disableCloseOnSelect}
      fullWidth={fullWidth}
      getOptionLabel={getOptionLabel}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      multiple={checkboxes ? (true as any) : multiple}
      renderInput={renderInput ?? defaultRenderInput}
      renderOption={renderOption ?? (checkboxes ? defaultCheckboxRenderOption : undefined)}
      size={size}
      {...props}
    />
  )
}
